// Money the office already holds, recorded against one bill. Shared by the
// matter's billing card and the invoice's own page - both settle the same
// bill, so both must ask the same questions and refresh the same readers.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recordManualPayment, type RecordPaymentInput } from "../api/caseInvoices";
import { fmtCents } from "../../../lib/money";

const REFERENCE_PLACEHOLDER: Record<RecordPaymentInput["method"], string> = {
  check: "Check #",
  cash: "Receipt #",
  zelle: "Confirmation #",
  other: "Reference",
};

interface Props {
  invoiceId: string;
  /** The bill's own matter - its lists are readers of this write. */
  caseId: string;
  number?: string | null;
  amountCents: number;
  /** The caller says where the outcome is announced; the copy is shared. */
  onRecorded?: (healed: boolean) => void;
  onCancel?: () => void;
}

/** What the office should read afterwards, in both places that record. */
export function recordedNote(
  healed: boolean,
  number: string | null | undefined,
  amountCents: number,
): string {
  const label = number ?? "The invoice";
  // A healed reply means the server kept an earlier row and dropped what
  // was just typed - saying "kept as is" reads as "your check number is
  // filed", which it is not.
  return healed
    ? `${label} was already recorded as paid by an earlier entry. Nothing was added and the details just typed were not saved - the earlier payment stands.`
    : `${label} paid in full - ${fmtCents(amountCents)} recorded.`;
}

export function RecordPaymentForm({
  invoiceId,
  caseId,
  number,
  amountCents,
  onRecorded,
  onCancel,
}: Props) {
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<RecordPaymentInput["method"]>("check");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [paidAt, setPaidAt] = useState("");
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: () =>
      recordManualPayment(invoiceId, {
        method,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(paidAt ? { paidAt } : {}),
      }),
    onSuccess: async ({ healed }) => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["case-invoices", caseId] }),
        // A settled part completes a plan, and the firm ledger, its totals
        // and this bill's own page all read the money that just moved.
        queryClient.invalidateQueries({ queryKey: ["case-plans", caseId] }),
        queryClient.invalidateQueries({ queryKey: ["billing"] }),
      ]);
      onRecorded?.(healed);
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Could not record it."),
  });

  return (
    <div className="cc-form-panel">
      <span className="cc-form-hint">
        Recording <span className="tnum">{fmtCents(amountCents)}</span> against{" "}
        {number ? <span className="tnum-mono">{number}</span> : "this invoice"} - the bill is
        settled in full, there is no partial amount.
      </span>

      <div className="cc-form-row">
        <label className="cc-form-field" style={{ width: 130 }}>
          <span className="cc-form-label">How it arrived</span>
          <select
            className="cc-field"
            aria-label="How the money arrived"
            value={method}
            onChange={(e) => setMethod(e.target.value as RecordPaymentInput["method"])}
          >
            <option value="check">Check</option>
            <option value="cash">Cash</option>
            <option value="zelle">Zelle</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label className="cc-form-field" style={{ width: 150 }}>
          <span className="cc-form-label">Reference</span>
          <input
            className="cc-field tnum-mono"
            aria-label="Payment reference"
            placeholder={REFERENCE_PLACEHOLDER[method]}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </label>

        <label className="cc-form-field" style={{ width: 165 }}>
          <span className="cc-form-label">Date received</span>
          <input
            className="cc-field"
            aria-label="Date received"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
          <span className="cc-form-hint">Blank means today.</span>
        </label>

        <label className="cc-form-field" style={{ flex: 1, minWidth: 200 }}>
          <span className="cc-form-label">Note</span>
          <input
            className="cc-field"
            aria-label="Internal note"
            placeholder="Internal - the client never sees this"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>

      <div className="cc-form-actions">
        <span className="cc-form-hint">
          For money the office already holds. Any open online checkout for this bill is cancelled.
        </span>
        <span style={{ flex: 1 }} />
        {onCancel && (
          <button type="button" className="cc-link-btn" onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="cc-btn-primary"
          disabled={record.isPending}
          onClick={() => record.mutate()}
        >
          {record.isPending ? "Recording..." : "Record"}
        </button>
      </div>

      {error && (
        <div role="alert" className="cc-form-error">
          {error}
        </div>
      )}
    </div>
  );
}
